<template>
  <div class="main">
    <Intro :editable="editable" :startup="startup" />
    <ScrollTopButton />

    <div v-if="canUserSeePrivateData" class="container">
      <el-tabs
        v-model="selectedTab"
        class="startup-profile-tabs main-content"
        type="card"
      >
        <el-tab-pane label="Overview" :name="TABS.OVERVIEW">
          <Overview :editable="editable" :startup="startup" />
        </el-tab-pane>
        <el-tab-pane label="Team" :name="TABS.TEAM">
          <Team :editable="editable" />
        </el-tab-pane>
        <el-tab-pane label="QA" :name="TABS.FAQ">
          <Faq :editable="editable" :faq="startup.faq" />
        </el-tab-pane>
        <el-tab-pane label="Documents" :name="TABS.DOCUMENTS">
          <DocumentList :startup="startup" :editable="editable" />
        </el-tab-pane>
        <el-tab-pane label="Video" :name="TABS.VIDEO" v-if="false">
          <startup-video :startup="startup" :editable="editable" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <div v-if="!canUserSeePrivateData" class="main-content">
      <div class="container">
        <div class="row">
          <div class="col-md-12">
            <div class="dashboard-box">
              <p class="text-muted">
                We are currently in Beta but feel free to
                <a href="mailto:office@vestbee.com">contact us</a>
                in order to get early access.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="editable">
      <el-tooltip content="Click to show more" placement="top">
        <el-button
          v-show="isProgressDrawerToggleVisible"
          class="progress-drawer-toggle"
          :class="{
            'is-success': report.isReady,
            'animated pulse': progressChanged,
          }"
          @click="showProgressDrawer = true"
        >
          <span class="progress-label">
            Progress: {{ report.progress }}%
            <ion-icon v-if="report.isReady" name="checkmark" />
          </span>
        </el-button>
      </el-tooltip>

      <el-drawer
        class="profile-progress-drawer"
        title="Profile completion progress report"
        :visible.sync="showProgressDrawer"
        direction="btt"
        :modal="true"
        size="500px"
      >
        <ProfileProgressDetails :startup="startup" :report="report" />
      </el-drawer>
    </template>

    <el-dialog
      :visible.sync="isProfileCompleteDialogVisible"
      width="50%"
      class="profile-completed-dialog"
      center
      :close-on-click-modal="false"
    >
      <el-progress
        class="well-done"
        type="circle"
        :percentage="100"
        status="success"
      />

      <p class="title">
        Great job!
      </p>
      <p class="caption">
        You've completed your company profile. While you can still fill in more
        information you are now ready to submit applications.
      </p>

      <router-link
        class="link-primary link-applications"
        :to="{ name: 'dashboardApplicationList' }"
      >
        Go to applications
      </router-link>

      <span slot="footer" class="dialog-footer">
        <el-button
          class="ok-button"
          type="primary"
          @click="isProfileCompleteDialogVisible = false"
        >
          Continue completing the profile
        </el-button>
        <!-- <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="dialogVisible = false">Confirm</el-button> -->
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Overview from './Overview.vue'
import Intro from './Intro.vue'
import Team from './Team.vue'
import DocumentList from './DocumentList.vue'
import StartupVideo from './StartupVideo.vue'
import Faq from './FAQ.vue'
import Tour from '@/services/onboarding'
import {
  PROFILE_PROGRESS_THRESHOLD,
  getReport,
} from '@/services/startup-report'
import ProfileProgressDetails from '@/components/startups/ProfileProgressDetails.vue'
import Storage from '@/services/storage'

const OVERVIEW = 'OVERVIEW'
const PITCH = 'PITCH'
const TEAM = 'TEAM'
const DOCUMENTS = 'DOCUMENTS'
const FAQ = 'FAQ'
const LINKS = 'LINKS'
const VIDEO = 'VIDEO'
const TABS = { PITCH, TEAM, DOCUMENTS, OVERVIEW, FAQ, LINKS, VIDEO }

export default {
  components: {
    Overview,
    Intro,
    DocumentList,
    StartupVideo,
    Team,
    Faq,
    ProfileProgressDetails,
  },
  props: {
    editable: { type: Boolean, default: () => false },
    canUserSeePrivateData: { type: Boolean, default: false },
  },
  data: () => ({
    allTabs: [TABS.OVERVIEW, TABS.PITCH, TABS.TEAM, TABS.DOCUMENTS, TABS.FAQ],
    TABS,
    showProgressDrawer: false,
    progressChanged: false,
    isProfileCompleteDialogVisible: false,
    tmpSelectedTab: null,
  }),
  watch: {
    report() {
      this.progressChanged = true
      setTimeout(() => {
        this.progressChanged = false
      }, 1000)
      if (this.report.progress >= PROFILE_PROGRESS_THRESHOLD) {
        this.showProfileCompletedDialog()
      }
    },
  },
  computed: {
    ...mapState({
      startup: (state) => state.startupProfile.startup,
      user: (state) => state.user.data,
      isClassificationEnabled: (state) =>
        state.startupProfileInvestorClassifies.isClassificationEnabled,
    }),
    selectedTab: {
      get() {
        return this.tmpSelectedTab
      },
      set(tab) {
        this.selectTab({ tab })
      },
    },
    isProgressDrawerToggleVisible() {
      return !this.showProgressDrawer
    },
    report() {
      return getReport(this.startup)
    },
  },
  created() {
    const tab = getTabFromUrlHash(this.$route.hash) || OVERVIEW
    this.selectTab({ tab, replaceHash: false })
    window.vm = this
  },
  mounted() {
    // example of how to start a tour:
    // getTour().start()
  },
  methods: {
    selectTab({ tab, replaceHash = true }) {
      this.tmpSelectedTab = tab
      if (replaceHash) {
        // OVERVIEW is default tab, do not append hash fragment for it
        let fragment = ''
        if (tab !== OVERVIEW) {
          fragment = '#' + tab.toLowerCase()
        }
        this.$router.replace({ ...this.$route, hash: fragment })
      }
    },
    getPitch() {
      return this.$refs.pitch.getPitch()
    },
    clearImageEditors() {
      this.$refs.pitch.clearImageEditors()
    },
    showProfileCompletedDialog() {
      const key = `VESTBEE_PROFILE_COMPLETED_DIALOG_ALREADY_SEEN_${this.startup.id}`
      const alreadySeen = Storage.get(key)
      if (!alreadySeen && this.user.isStartup && this.editable) {
        this.isProfileCompleteDialogVisible = true
        Storage.set(key, true)
      }
    },
  },
}

function getTabFromUrlHash(hash) {
  if (hash && hash[0] === '#') {
    const tabName = hash.substring(1).toUpperCase()
    if (TABS[tabName]) {
      return TABS[tabName]
    } else {
      return null
    }
  }
}

function getTour() {
  return new Tour([
    {
      arrow: true,
      title: 'Foo',
      text: 'Bar bar lorem ipsum',
      selector: '#tab-OVERVIEW',
    },
    {
      title: 'Foo',
      text: 'Bar bar lorem ipsum',
      selector: '.my-application-sidenav-link',
    },
  ])
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.main {
  margin-top: 64px;
}

.startup-profile-tabs {
  .el-tabs__item {
    border-bottom: none;
  }
  .el-tabs__item.is-active {
    background-color: $yellow;
    font-weight: 800;
  }
}

.progress-drawer-toggle {
  position: fixed;
  bottom: 0;
  left: 50%;
  margin-left: -150px;
  margin-bottom: -5px;
  width: 400px;
  height: 60px;
  z-index: 999;
  background-color: white;

  .progress-label {
    color: $danger;
  }

  &.is-success {
    .progress-label {
      color: $green;
    }
  }
}

.profile-progress-drawer {
  .el-drawer__body {
    padding: 5px 20px;
    color: $muted;
  }
}

.profile-completed-dialog {
  .el-dialog__body {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .title {
      margin: 20px 0 20px;
      font-size: 30px;
      font-weight: 600;
    }

    .caption {
      color: $muted;
      font-size: 16px;
      padding: 10px 30px;
      word-break: normal;
      text-align: center;
    }

    .link-applications {
      font-size: 16px;
    }
  }
}
</style>
